import { useEffect, useRef, useState } from "react";
import { cctvRequester } from "App";
import styles from './CCTVMonitor.module.css';



const CameraView = ({ camera, token }) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isVisible, setIsVisible] = useState(true);
    const previousBlobUrl = useRef(null);
    const previousBlob = useRef(null);
    const intervalRef = useRef(null);
    const observerRef = useRef(null);
    const containerRef = useRef(null);

    // Intersection Observer 설정
    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    setIsVisible(entry.isIntersecting);
                });
            },
            { threshold: 0.1 }
        );

        if (containerRef.current) {
            observerRef.current.observe(containerRef.current);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    // 이미지 fetching 로직
    useEffect(() => {
        const controller = new AbortController();

        const fetchImage = async () => {
            try {
                const imageBlob = await cctvRequester.getCameraImage(camera.id, token, {
                    signal: controller.signal
                });

                // 이미지가 이전과 다른 경우에만 업데이트
                if (!previousBlob.current || previousBlob.current !== imageBlob) {
                    // 이전 Blob URL 정리
                    if (previousBlobUrl.current) {
                        URL.revokeObjectURL(previousBlobUrl.current);
                    }

                    const newUrl = URL.createObjectURL(imageBlob);

                    // 컴포넌트가 마운트된 상태에서만 상태 업데이트
                    if (!controller.signal.aborted) {
                        setImageSrc(newUrl);
                        previousBlobUrl.current = newUrl;
                        previousBlob.current = imageBlob;
                    }
                }
            } catch (err) {
                if (err.name === 'AbortError') {
                    console.log('Image fetch aborted');
                } else {
                    console.error(`Failed to fetch camera ${camera.id} image:`, err);
                }
            }
        };

        // 컴포넌트가 보이는 경우에만 이미지 업데이트
        if (isVisible || isFullscreen) {
            fetchImage(); // 초기 이미지 로드
            intervalRef.current = setInterval(fetchImage, 300); // 0.3초마다 요청
        }

        return () => {
            // 인터벌 및 진행 중인 요청 취소
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            controller.abort();

            // Blob URL 정리
            if (previousBlobUrl.current) {
                URL.revokeObjectURL(previousBlobUrl.current);
                previousBlobUrl.current = null;
            }
        };
    }, [camera.id, token, isVisible, isFullscreen]);

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    return (
        <>
            <div
                ref={containerRef}
                className={`${styles.cameraContainer} ${styles.cameraClickable}`}
                onClick={toggleFullscreen}
            >
                <div className={styles.cameraLabel}>
                    {camera.name} - Store {camera.store_code}
                </div>
                {imageSrc ? (
                    <img
                        src={imageSrc}
                        alt={`Camera ${camera.id}`}
                        className={styles.cameraImage}
                    />
                ) : (
                    <div className={styles.loadingContainer}>
                        Loading...
                    </div>
                )}
            </div>

            {isFullscreen && (
                <div className={styles.fullscreenOverlay} onClick={toggleFullscreen}>
                    <div className={styles.fullscreenContainer}>
                        <div className={styles.fullscreenLabel}>
                            {camera.name} - Store {camera.store_code}
                        </div>
                        <button
                            className={styles.closeButton}
                            onClick={toggleFullscreen}
                        >
                            ✕
                        </button>
                        {imageSrc && (
                            <img
                                src={imageSrc}
                                alt={`Camera ${camera.id} fullscreen`}
                                className={styles.fullscreenImage}
                            />
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default CameraView;